import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/components/header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useErrorReporter } from "@/components/error-reporter";
import {
  RankedBarChart,
  SessionStatusDonut,
  SessionsTrendCard,
} from "@/components/analytics/charts";
import { BarChart3, Activity, ArrowRight, Sparkles } from "lucide-react";

export const Route = createFileRoute("/_authenticated/quizzes/analytics")({
  component: AllQuizzesAnalyticsPage,
});

function AllQuizzesAnalyticsPage() {
  const { user } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { reportError } = useErrorReporter();

  useEffect(() => {
    async function load() {
      if (!user) return;
      const { data, error } = await supabase
        .from("quizzes")
        .select("*, questions(count)")
        .eq("creator_id", user.id)
        .order("created_at", { ascending: false });
      if (error) reportError(error, { area: "analytics.all" });

      const quizList = data ?? [];
      setQuizzes(quizList);

      if (quizList.length > 0) {
        const { data: sessionRows, error: sessionError } = await supabase
          .from("sessions")
          .select("id, quiz_id, status, created_at")
          .in(
            "quiz_id",
            quizList.map((q) => q.id),
          );
        if (sessionError) reportError(sessionError, { area: "analytics.all.sessions" });
        setSessions(sessionRows ?? []);
      } else {
        setSessions([]);
      }

      setLoading(false);
    }
    load();
  }, [user?.id]);

  const playsByQuiz = useMemo(() => {
    const counts = new Map();
    for (const s of sessions) counts.set(s.quiz_id, (counts.get(s.quiz_id) ?? 0) + 1);
    return quizzes
      .map((q) => ({ name: q.title, value: counts.get(q.id) ?? 0 }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [quizzes, sessions]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg">
        <Header />
        <div className="container mx-auto px-4 py-10 max-w-6xl">
          <p className="text-muted">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg">
      <Header />
      <div className="container mx-auto px-4 py-10 max-w-6xl space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <BarChart3 className="size-8 text-accent" />
              Quiz Analytics
            </h1>
            <p className="text-muted">Overview of all your quizzes' performance</p>
          </div>
          <Button asChild variant="outline">
            <Link to="/dashboard">Back to dashboard</Link>
          </Button>
        </div>

        {quizzes.length === 0 ? (
          <Card className="p-6 text-center bg-gradient-card border-2 sm:p-12">
            <Sparkles className="size-12 mx-auto text-accent" />
            <h2 className="text-xl font-bold mt-4">No quizzes yet</h2>
            <p className="text-muted mt-1">Create your first quiz to see analytics here.</p>
            <Button asChild className="mt-4 bg-gradient-primary">
              <Link to="/dashboard">Go to dashboard</Link>
            </Button>
          </Card>
        ) : (
          <>
            <div className="grid lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2">
                <SessionsTrendCard sessions={sessions} />
              </div>
              <SessionStatusDonut sessions={sessions} />
            </div>

            <RankedBarChart
              title="Most played quizzes"
              caption="Sessions hosted per quiz"
              rows={playsByQuiz}
              unit="plays"
            />

            <div>
              <h2 className="font-bold text-lg flex items-center gap-2">
                <BarChart3 className="size-5 text-accent" /> Your quizzes
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                {quizzes.map((q) => (
                  <Card
                    key={q.id}
                    className="p-5 hover:shadow-elegant transition-all bg-gradient-card border-2 group"
                  >
                    <div className="flex justify-between items-start gap-2 mb-2">
                      <h3 className="font-bold text-lg line-clamp-2 flex-1">{q.title}</h3>
                      <Badge variant={q.visibility === "public" ? "default" : "secondary"}>
                        {q.visibility}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted line-clamp-2 mb-3">
                      {q.description ?? "No description"}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted mb-4">
                      <div className="flex items-center gap-1">
                        <Activity className="size-3" />
                        <span>{q.questions?.[0]?.count ?? 0} questions</span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="w-full bg-gradient-primary group-hover:scale-[1.02] transition-transform"
                      asChild
                    >
                      <Link to="/quizzes/$id/analytics" params={{ id: q.id }}>
                        View detailed analytics <ArrowRight className="size-4 ml-2" />
                      </Link>
                    </Button>
                  </Card>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
